import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, loadRazorpayScript } from "@/lib/api";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { CART } from "@/constants/testIds";
import { toast } from "sonner";

export default function CartPage() {
  const { items, setQty, remove, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState({});
  const [step, setStep] = useState("cart"); // cart | shipping | payment
  const [shipping, setShipping] = useState({ name: "", phone: "", address_line1: "", city: "", state: "", pincode: "", country: "India" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadAll() {
      const map = {};
      for (const it of items) {
        try {
          // fetch by id — use /products with all and find (fallback for missing product)
          const { data } = await api.get(`/products`, { params: { limit: 200 } });
          for (const p of data) map[p.id] = p;
          break;
        } catch { /* ignore */ }
      }
      setProducts(map);
    }
    if (items.length > 0) loadAll();
  }, [items]);

  const detailedItems = items.map((it) => ({ ...it, product: products[it.product_id] })).filter((x) => x.product);
  const subtotal = detailedItems.reduce((s, x) => s + x.product.price * x.quantity, 0);
  const shippingFee = subtotal >= 999 || subtotal === 0 ? 0 : 79;
  const total = subtotal + shippingFee;

  async function placeOrder(method) {
    if (!user) { nav("/login", { state: { from: "/cart" } }); return; }
    setLoading(true);
    try {
      const orderItems = detailedItems.map((it) => ({
        product_id: it.product_id,
        quantity: it.quantity,
        price: it.product.price,
        name: it.product.name,
        image: it.product.images?.[0] || "",
      }));
      const { data } = await api.post("/orders", {
        items: orderItems,
        shipping,
        subtotal,
        shipping_fee: shippingFee,
        total,
        payment_method: method,
      });

      if (method === "cod") {
        toast.success("Order placed! ID: " + data.order_number);
        await clear();
        nav(`/track-order?id=${data.order_number}`);
        return;
      }

      // Razorpay
      await loadRazorpayScript();
      if (!data.razorpay_key_id) {
        // Demo mode — simulate success
        await api.post("/orders/verify-payment", {
          order_id: data.order_id,
          razorpay_order_id: data.razorpay_order_id,
          razorpay_payment_id: `pay_MOCK${Math.random().toString(36).slice(2, 10)}`,
          razorpay_signature: "mock_signature",
        });
        toast.success("Payment successful (demo mode). Order: " + data.order_number);
        await clear();
        nav(`/track-order?id=${data.order_number}`);
        return;
      }
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: "SSR Innovations",
        description: "Order " + data.order_number,
        order_id: data.razorpay_order_id,
        prefill: { name: shipping.name, contact: shipping.phone, email: user.email },
        theme: { color: "#0055FF" },
        handler: async (resp) => {
          await api.post("/orders/verify-payment", {
            order_id: data.order_id,
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Payment successful. Order: " + data.order_number);
          await clear();
          nav(`/track-order?id=${data.order_number}`);
        },
      });
      rzp.open();
    } catch (e) {
      toast.error("Order failed");
    } finally { setLoading(false); }
  }

  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground" />
        <h1 className="mt-6 font-heading font-medium text-3xl">Your cart is empty</h1>
        <p className="mt-2 text-muted-foreground">Explore our latest arrivals and find something you'll love.</p>
        <Link to="/shop" className="mt-6 inline-flex items-center h-11 px-6 rounded-full bg-electric text-white text-sm font-medium hover:bg-electric-600 transition-colors" data-testid="cart-empty-shop">Shop now</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid lg:grid-cols-[1fr_360px] gap-10">
      <div>
        <h1 className="font-heading font-medium text-4xl mb-8">Your cart</h1>
        <div className="space-y-4">
          {detailedItems.map((it) => (
            <div key={it.product_id} data-testid={`cart-row-${it.product.slug}`} className="flex gap-4 p-4 rounded-2xl border border-border">
              <Link to={`/product/${it.product.slug}`} className="w-24 h-24 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
                {it.product.images?.[0] && <img src={it.product.images[0]} alt="" className="w-full h-full object-cover" />}
              </Link>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{it.product.subcategory.replace(/-/g, " ")}</div>
                <Link to={`/product/${it.product.slug}`} className="text-sm font-medium block hover:text-electric truncate">{it.product.name}</Link>
                <div className="mt-2 flex items-center gap-3">
                  <div className="flex items-center gap-1 border border-border rounded-full p-1">
                    <button data-testid={CART.itemDec + `-${it.product.slug}`} onClick={() => setQty(it.product_id, it.quantity - 1)} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                    <div className="w-6 text-center text-xs tabular-nums">{it.quantity}</div>
                    <button data-testid={CART.itemInc + `-${it.product.slug}`} onClick={() => setQty(it.product_id, it.quantity + 1)} className="w-7 h-7 rounded-full hover:bg-secondary flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                  </div>
                  <button data-testid={CART.itemRemove + `-${it.product.slug}`} onClick={() => remove(it.product_id)} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>
                </div>
              </div>
              <div className="text-sm font-medium tabular-nums whitespace-nowrap">₹{(it.product.price * it.quantity).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>

      <aside className="lg:sticky lg:top-24 lg:self-start p-6 rounded-2xl border border-border bg-card">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">Summary</div>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">₹{subtotal.toLocaleString()}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span className="tabular-nums">{shippingFee === 0 ? "Free" : `₹${shippingFee}`}</span></div>
          <div className="flex justify-between border-t pt-3 mt-3"><span className="font-medium">Total</span><span className="font-medium tabular-nums">₹{total.toLocaleString()}</span></div>
        </div>

        {step === "cart" ? (
          <button data-testid={CART.checkoutBtn} onClick={() => user ? setStep("shipping") : nav("/login", { state: { from: "/cart" } })} className="mt-6 w-full h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 transition-colors">
            {user ? "Checkout" : "Sign in to checkout"}
          </button>
        ) : step === "shipping" ? (
          <div className="mt-4 space-y-2">
            {[
              ["name", "Full name"], ["phone", "Phone"], ["address_line1", "Address"], ["city", "City"], ["state", "State"], ["pincode", "PIN"],
            ].map(([k, ph]) => (
              <input key={k} required placeholder={ph} value={shipping[k]} onChange={(e) => setShipping((s) => ({ ...s, [k]: e.target.value }))} className="w-full h-10 px-3 rounded-lg bg-secondary text-sm outline-none focus:ring-2 focus:ring-electric" data-testid={`ship-${k}`} />
            ))}
            <button data-testid="ship-continue" disabled={!shipping.name || !shipping.phone || !shipping.address_line1} onClick={() => setStep("payment")} className="w-full h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 disabled:opacity-50 transition-colors">Continue to payment</button>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            <button data-testid="pay-razorpay" disabled={loading} onClick={() => placeOrder("razorpay")} className="w-full h-11 rounded-full bg-electric text-white font-medium hover:bg-electric-600 disabled:opacity-60 transition-colors">Pay ₹{total.toLocaleString()} · Razorpay</button>
            <button data-testid="pay-cod" disabled={loading} onClick={() => placeOrder("cod")} className="w-full h-11 rounded-full border border-border font-medium hover:border-foreground disabled:opacity-60 transition-colors">Cash on Delivery</button>
            <button onClick={() => setStep("shipping")} className="w-full text-xs text-muted-foreground hover:text-foreground">← Back</button>
          </div>
        )}
      </aside>
    </div>
  );
}
